import { Anchor, Badge, Button, Checkbox, CopyButton, Group, Modal, MultiSelect, PasswordInput, Stack, Table, Tabs, Text, TextInput, Tooltip } from "@mantine/core";
import { IconCopy } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { PhoneInput } from "../../components/PhoneInput";
import { CitySelect } from "../../components/CitySelect";
import { CITY_SELECT_COPY } from "../../lib/cities";
import { DataTableShell, FilterBar, PageHeader, SortableTh, StatusBadge, useInfinitePage, useTableSort } from "../../components/ui";
import { CALL_COPY_NUMBER_LABEL, CALL_NUMBER_COPIED } from "../../components/explore/constants";
import { PhoneDialLinks } from "../../components/explore/CallDialQr";
import { adminApi } from "../../lib/api";
import { isEmailVerified, isPhoneVerified } from "../../lib/accountVerification";
import { NAV_ICONS } from "../../lib/navCopy";
import { ApiError } from "../../lib/apiClient";
import { formatDate } from "../../lib/format";
import { catalogFuzzyFilter } from "../../lib/catalogFuzzyFilter";
import { useQueryFilters } from "../../lib/useQueryFilters";
import { IDENTITY_KYC_REQUIRED_SLOTS, kycDocumentProgressLabel } from "../../lib/kyc";
import { useSegmentCatalog } from "../../lib/useSegmentCatalog";
import { renderSegmentOption } from "../../components/SegmentOptionLabel";
import { validateIndianPhoneFieldOptional, toPhoneForApi } from "../../lib/phone";
import type { KycStatus, Segment } from "../../types/api";

export const RECRUITERS_LIST_COPY = {
  name: "Name",
  lastLogin: "Last login",
  contact: "Contact",
  status: "Status",
  copyEmail: "Copy email",
  copyPhone: "Copy phone",
  empty: "—",
  emailVerified: "Email",
  phoneVerified: "Phone",
  emailVerifiedAria: "Email verified",
  phoneVerifiedAria: "Phone verified",
  docsMissing: "Docs missing",
} as const;

export const CONTACT_EMAIL_MAX = "var(--axio-unit-180)";
export const NAME_COLUMN_MIN = "var(--axio-unit-180)";
const COPY_ICON_SIZE = "var(--axio-unit-14)";
const CONTACT_COPY_SHRINK = "var(--axio-number-zero)";
const RECRUITER_TABS = [
  { value: "pending", label: "Needs documents" },
  { value: "submitted", label: "Ready to review" },
  { value: "verified", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
] as const;
const RECRUITER_TAB_DEFAULT = RECRUITER_TABS[0].value;
const DOCS_MISSING_ON = "1";
const RECRUITER_FILTER_DEFAULTS = { tab: RECRUITER_TAB_DEFAULT, docs_missing: "" };
const RECRUITER_FILTER_ALLOWED = {
  tab: RECRUITER_TABS.map((item) => item.value),
  docs_missing: [DOCS_MISSING_ON],
};
const RECRUITER_FILTER_OPTIONS = { allowed: RECRUITER_FILTER_ALLOWED };

function ContactVerifiedBadge({ label, ariaLabel }: { label: string; ariaLabel: string }) {
  return (
    <Badge size="xs" color="green" variant="light" w="fit-content" aria-label={ariaLabel}>
      {label}
    </Badge>
  );
}

function ContactCopyButton({ value, label }: { value: string; label: string }) {
  return (
    <CopyButton value={value}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? CALL_NUMBER_COPIED : CALL_COPY_NUMBER_LABEL}>
          <Anchor
            component="button"
            type="button"
            size="xs"
            c="dimmed"
            aria-label={label}
            display="inline-flex"
            style={{ flexShrink: CONTACT_COPY_SHRINK }}
            onClick={(event) => {
              event.stopPropagation();
              copy();
            }}
          >
            <IconCopy size={COPY_ICON_SIZE} />
          </Anchor>
        </Tooltip>
      )}
    </CopyButton>
  );
}

export function RecruitersPage() {
  const queryClient = useQueryClient();
  const { flatOptions: segmentOptions } = useSegmentCatalog();
  const recruiterFilters = useQueryFilters(RECRUITER_FILTER_DEFAULTS, RECRUITER_FILTER_OPTIONS);
  const tab = recruiterFilters.values.tab;
  const docsMissing = recruiterFilters.values.docs_missing === DOCS_MISSING_ON;
  const [createOpen, setCreateOpen] = useState(false);

  const kycFilter = tab === "all" ? undefined : (tab as KycStatus);

  const recruitersQuery = useInfinitePage(["admin-recruiters", tab, docsMissing], (page, pageSize) =>
    adminApi.listRecruiters({
      kyc_status: kycFilter,
      docs_missing: tab === "verified" && docsMissing ? true : undefined,
      page,
      page_size: pageSize,
    }),
  );

  const seatsQuery = useQuery({
    queryKey: ["admin-calling-seats"],
    queryFn: () => adminApi.listCallingSeats(),
  });

  const seatByRecruiter = new Map(
    (seatsQuery.data ?? [])
      .filter((s) => s.is_active)
      .map((s) => [s.recruiter_id, s] as const),
  );

  const grantSeatMutation = useMutation({
    mutationFn: (recruiterId: string) => adminApi.grantCallingSeat(recruiterId, "Granted from admin recruiters page"),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Explore / calling seat granted" });
      queryClient.invalidateQueries({ queryKey: ["admin-calling-seats"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Failed to grant seat",
      });
    },
  });

  const revokeSeatMutation = useMutation({
    mutationFn: (recruiterId: string) => adminApi.revokeCallingSeat(recruiterId),
    onSuccess: () => {
      notifications.show({ color: "orange", message: "Calling seat revoked" });
      queryClient.invalidateQueries({ queryKey: ["admin-calling-seats"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Failed to revoke seat",
      });
    },
  });

  const createForm = useForm({
    initialValues: {
      full_name: "",
      email: "",
      password: "",
      phone: "",
      city: "",
      segments: [] as string[],
      mark_kyc_verified: false,
    },
    validate: {
      full_name: (v) => (v.trim().length >= 2 ? null : "Required"),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Valid email required"),
      password: (v) => (v.length >= 8 ? null : "Min 8 characters"),
      phone: validateIndianPhoneFieldOptional,
      city: (v) => (v.trim() ? null : CITY_SELECT_COPY.requiredError),
      segments: (v) => (v.length > 0 ? null : "Select at least one segment"),
    },
  });

  const createMutation = useMutation({
    mutationFn: adminApi.createRecruiter,
    onSuccess: () => {
      notifications.show({ color: "green", title: "Created", message: "Recruiter account created." });
      queryClient.invalidateQueries({ queryKey: ["admin-recruiters"] });
      createForm.reset();
      setCreateOpen(false);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  const recruiters = recruitersQuery.items;
  const { sorted: sortedRecruiters, sortKey, direction, toggle } = useTableSort(recruiters, {
    name: (r) => r.user.full_name,
    contact: (r) => `${r.user.email} ${r.user.phone ?? ""}`,
    city: (r) => r.city,
    company: (r) => r.consultancy_name ?? "",
    seat: (r) => seatByRecruiter.has(r.id),
    registered: (r) => r.created_at,
    lastLogin: (r) => r.user.last_login_at,
    status: (r) => r.kyc_status,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.recruiters}
        title="Recruiter Approvals"
        subtitle="Create recruiters, approve KYC, and grant Explore calling seats for the candidate queue."
        actions={
          <Button color="violet" onClick={() => setCreateOpen(true)}>
            + Create recruiter
          </Button>
        }
      />

      <FilterBar>
        <Tabs value={tab} onChange={(value) => value && recruiterFilters.set({ tab: value })}>
          <Tabs.List>
            {RECRUITER_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        {tab === "verified" && (
          <Checkbox
            label={RECRUITERS_LIST_COPY.docsMissing}
            checked={docsMissing}
            onChange={(event) =>
              recruiterFilters.set({ docs_missing: event.currentTarget.checked ? DOCS_MISSING_ON : "" })
            }
          />
        )}
      </FilterBar>

      <DataTableShell
        loading={recruitersQuery.isLoading}
        error={
          recruitersQuery.isError
            ? recruitersQuery.error instanceof Error
              ? recruitersQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void recruitersQuery.refetch()}
        hasMore={recruitersQuery.hasMore}
        loadingMore={recruitersQuery.loadingMore}
        onLoadMore={recruitersQuery.loadMore}
        isEmpty={recruiters.length === 0}
        empty={{
          icon: NAV_ICONS.recruiters,
          title: "No recruiters in this category",
          description: "Try switching tabs to see recruiters in other KYC states.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh
                columnKey="name"
                sortKey={sortKey}
                direction={direction}
                onSort={toggle}
                miw={NAME_COLUMN_MIN}
              >
                {RECRUITERS_LIST_COPY.name}
              </SortableTh>
              <SortableTh columnKey="contact" sortKey={sortKey} direction={direction} onSort={toggle}>
                {RECRUITERS_LIST_COPY.contact}
              </SortableTh>
              <SortableTh columnKey="city" sortKey={sortKey} direction={direction} onSort={toggle}>
                City
              </SortableTh>
              <SortableTh columnKey="company" sortKey={sortKey} direction={direction} onSort={toggle}>
                Company
              </SortableTh>
              <SortableTh columnKey="seat" sortKey={sortKey} direction={direction} onSort={toggle}>
                Explore seat
              </SortableTh>
              <SortableTh columnKey="registered" sortKey={sortKey} direction={direction} onSort={toggle}>
                Registered
              </SortableTh>
              <SortableTh columnKey="lastLogin" sortKey={sortKey} direction={direction} onSort={toggle}>
                {RECRUITERS_LIST_COPY.lastLogin}
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                {RECRUITERS_LIST_COPY.status}
              </SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedRecruiters.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td fw="var(--axio-font-weight-medium)" miw={NAME_COLUMN_MIN}>
                  <Text component={Link} to={`/admin/recruiters/${r.id}`} fw="var(--axio-font-weight-medium)">
                    {r.user.full_name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Stack gap="var(--axio-unit-1)">
                    {(isEmailVerified(r.user) || isPhoneVerified(r.user)) && (
                      <Group gap="var(--axio-unit-6)" wrap="nowrap">
                        {isEmailVerified(r.user) && (
                          <ContactVerifiedBadge
                            label={RECRUITERS_LIST_COPY.emailVerified}
                            ariaLabel={RECRUITERS_LIST_COPY.emailVerifiedAria}
                          />
                        )}
                        {isPhoneVerified(r.user) && (
                          <ContactVerifiedBadge
                            label={RECRUITERS_LIST_COPY.phoneVerified}
                            ariaLabel={RECRUITERS_LIST_COPY.phoneVerifiedAria}
                          />
                        )}
                      </Group>
                    )}
                    <Group gap="var(--axio-unit-6)" wrap="nowrap">
                      <Text
                        size="sm"
                        truncate="end"
                        maw={CONTACT_EMAIL_MAX}
                        miw="var(--axio-unit-0)"
                        title={r.user.email}
                      >
                        {r.user.email}
                      </Text>
                      <ContactCopyButton value={r.user.email} label={RECRUITERS_LIST_COPY.copyEmail} />
                    </Group>
                    <Group gap="var(--axio-unit-6)" wrap="nowrap">
                      <Text size="xs" c="dimmed" className="tabular">
                        {r.user.phone ?? RECRUITERS_LIST_COPY.empty}
                      </Text>
                      {r.user.phone ? <PhoneDialLinks phone={r.user.phone} /> : null}
                    </Group>
                  </Stack>
                </Table.Td>
                <Table.Td>{r.city ?? "—"}</Table.Td>
                <Table.Td>{r.consultancy_name ?? "—"}</Table.Td>
                <Table.Td>
                  {seatByRecruiter.has(r.id) ? (
                    <Group gap="xs">
                      <Badge color="teal" variant="light">
                        Active
                      </Badge>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="red"
                        loading={revokeSeatMutation.isPending && revokeSeatMutation.variables === r.id}
                        onClick={() => revokeSeatMutation.mutate(r.id)}
                      >
                        Revoke
                      </Button>
                    </Group>
                  ) : (
                    <Button
                      size="compact-xs"
                      variant="default"
                      loading={grantSeatMutation.isPending && grantSeatMutation.variables === r.id}
                      onClick={() => grantSeatMutation.mutate(r.id)}
                    >
                      Grant Explore
                    </Button>
                  )}
                </Table.Td>
                <Table.Td>{formatDate(r.created_at)}</Table.Td>
                <Table.Td>{formatDate(r.user.last_login_at)}</Table.Td>
                <Table.Td>
                  <Stack gap="var(--axio-unit-1)">
                    <StatusBadge status={r.kyc_status} />
                    <Text size="xs" c="dimmed" className="tabular">
                      {kycDocumentProgressLabel(
                        r.kyc_filled_count ?? 0,
                        r.kyc_required_count ?? IDENTITY_KYC_REQUIRED_SLOTS.length,
                      )}
                    </Text>
                    {r.kyc_status === "verified" && !r.kyc_documents_complete && (
                      <Badge size="xs" color="blue" variant="light" w="fit-content">
                        {RECRUITERS_LIST_COPY.docsMissing}
                      </Badge>
                    )}
                  </Stack>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="Create recruiter" centered>
        <form
          onSubmit={createForm.onSubmit((values) =>
            createMutation.mutate({
              full_name: values.full_name,
              email: values.email,
              password: values.password,
              phone: values.phone ? toPhoneForApi(values.phone) : undefined,
              city: values.city || undefined,
              segments: values.segments as Segment[],
              kyc_status: values.mark_kyc_verified ? "verified" : "pending",
            }),
          )}
        >
          <Stack>
            <TextInput label="Full name" required {...createForm.getInputProps("full_name")} />
            <TextInput label="Email" required {...createForm.getInputProps("email")} />
            <PhoneInput
              label="Phone (WhatsApp)"
              value={createForm.values.phone}
              onChange={(value) => createForm.setFieldValue("phone", value)}
              error={createForm.errors.phone}
            />
            <PasswordInput label="Password" required {...createForm.getInputProps("password")} />
            <CitySelect
              label={CITY_SELECT_COPY.label}
              withAsterisk
              clearable={false}
              value={createForm.values.city}
              onChange={(city) => createForm.setFieldValue("city", city)}
              error={createForm.errors.city}
            />
            <MultiSelect
              label="Segments"
              withAsterisk
              searchable
              clearable
              data={segmentOptions}
              renderOption={renderSegmentOption}
              filter={catalogFuzzyFilter}
              limit={40}
              nothingFoundMessage="No matching segments"
              {...createForm.getInputProps("segments")}
            />
            <Checkbox
              label="Mark KYC as verified"
              {...createForm.getInputProps("mark_kyc_verified", { type: "checkbox" })}
            />
            <Button type="submit" color="violet" loading={createMutation.isPending}>
              Create recruiter
            </Button>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
